import React, { useState } from 'react';
import clsx from 'clsx';
import Icon from '@components/atoms/icon/icon';
import Environment from '@config/environment';
import { useTranslation } from 'next-i18next';
import { useAnalytics } from '@context/analytics.context';
import { Field, FieldProps, Form, Formik } from 'formik';
import * as Yup from 'yup';
import useLocalizationQuery from '@hooks/localization/use-localization';
import ValidationModal from '../validation-modal/validation-modal';
import InputComponent from './input/input';
import styles from './form.module.scss';

const isProduction = Environment.envName === 'production';
const isDebug = Environment.envName === 'dev';

const FORM_FIELD_MATCHER = isProduction
  ? {
      legalFormCompany: '00N6700000DWiOq',
      taxIdNumber: '00N6700000DWiOu',
      companyRegistrationNumber: '00N6700000DWiOm',
      inventoryManagmentSystem: '00N6700000DWiOo',
      notesFromSeller: '00N6700000DWiOs',
      country: '00N6700000DWiPA',
      inventoryMix: '00N6700000DWiOp',
      businessType: '00N6700000DWiOl',
      socialMediaAccount: '00N6700000DWiOr',
      language: '00N6700000DWiOt',
      depositForecast: '00N6700000DWiOn',
    }
  : {
      legalFormCompany: '00N1x00000EFcQO',
      taxIdNumber: '00N1x00000EFe7T',
      companyRegistrationNumber: '00N1x00000EFe7d',
      inventoryManagmentSystem: '00N1x00000EFfvh',
      notesFromSeller: '00N1x00000EFg64',
      country: '00N1x00000EFhwA',
      inventoryMix: '00N1x00000EFibZ',
      businessType: '00N1x000004AFFR',
      socialMediaAccount: '00N1x000004AG3j',
      language: '00N1x000004AG3y',
      depositForecast: '00N1x000004CwHj',
    };

const Schema = Yup.lazy(() => {
  const objectShape = {
    oid: Yup.string(),
    retURL: Yup.string(),
    lead_source: Yup.string(),
    debugEmail: Yup.string(),
    email: Yup.string().email('Invalid email').required('Required'),
    first_name: Yup.string().required('Required'),
    last_name: Yup.string().required('Required'),
    title: Yup.string().required('Required'),
    company: Yup.string().required('Required'),
    state: Yup.string(),
    city: Yup.string().required('Required'),
    street: Yup.string().required('Required'),
    zip: Yup.string().required('Required'),
    url: Yup.string(),
    isSecondHandSeller: Yup.boolean().required('Required'),
  };

  objectShape[FORM_FIELD_MATCHER.legalFormCompany] = Yup.string();
  objectShape[FORM_FIELD_MATCHER.taxIdNumber] = Yup.string().required('Required');
  objectShape[FORM_FIELD_MATCHER.companyRegistrationNumber] = Yup.string().required('Required');
  objectShape[FORM_FIELD_MATCHER.inventoryManagmentSystem] = Yup.string();
  objectShape[FORM_FIELD_MATCHER.notesFromSeller] = Yup.string();
  objectShape[FORM_FIELD_MATCHER.country] = Yup.string().required('Required');
  objectShape[FORM_FIELD_MATCHER.inventoryMix] = Yup.array().of(Yup.string()).required('Required');
  objectShape[FORM_FIELD_MATCHER.businessType] = Yup.string().required('Required');
  objectShape[FORM_FIELD_MATCHER.socialMediaAccount] = Yup.string();
  objectShape[FORM_FIELD_MATCHER.language] = Yup.string().required('Required');
  objectShape[FORM_FIELD_MATCHER.depositForecast] = Yup.string().required('Required');

  return Yup.object().shape(objectShape);
});

const hiddenInputs = {
  oid: isProduction ? '00D0X000000sv3i' : '00D1x0000008gnS',
  retURL: 'http://',
  lead_source: 'Web to Lead',
  debug: !isDebug ? '0' : '1',
  debugEmail: !isDebug ? '' : 'shelton.wilson@vestiairecollective.com',
};

const businessTypeOptions = [
  { name: 'PRO_SELLERS.FORM.LABELS.BUSINESS_TYPE.CONSIGNMENT', id: 'Consignment', value: 'Consignment' },
  { name: 'PRO_SELLERS.FORM.LABELS.BUSINESS_TYPE.BUYING', id: 'Buying', value: 'Buying' },
  {
    name: 'PRO_SELLERS.FORM.LABELS.BUSINESS_TYPE.CONSIGNMENT_BUYING',
    id: 'Consignment &amp; Buying',
    value: 'Consignment &amp; Buying',
  },
  { name: 'PRO_SELLERS.FORM.LABELS.BUSINESS_TYPE.OTHER', id: 'Others', value: 'Others' },
];

const languageOptions = [
  { name: 'PRO_SELLERS.FORM.LABELS.LANGUAGE.ENGLISH', id: 'English', value: 'EN' },
  { name: 'PRO_SELLERS.FORM.LABELS.LANGUAGE.FRENCH', id: 'French', value: 'FR' },
  { name: 'PRO_SELLERS.FORM.LABELS.LANGUAGE.GERMAN', id: 'German', value: 'DE' },
  { name: 'PRO_SELLERS.FORM.LABELS.LANGUAGE.ITALIAN', id: 'Italian', value: 'IT' },
  { name: 'PRO_SELLERS.FORM.LABELS.LANGUAGE.SPANISH', id: 'Spanish', value: 'ES' },
  { name: 'PRO_SELLERS.FORM.LABELS.LANGUAGE.JAPANESE', id: 'Japanese', value: 'JP' },
];

const BottomInformation: React.FC = () => {
  const { t } = useTranslation('professional-sellers');
  return (
    <div className={clsx(styles.proSellersForm__form__bottomInfo, 'vc-text-s')}>
      <p>{t('PRO_SELLERS.FORM.BOTTOM_INFO')}</p>
      <p>{t('PRO_SELLERS.FORM.REQUIRED_FIELDS')}</p>
    </div>
  );
};

type ProfessionalSellersFormProps = {
  onCTAClick: (clickLocation: 'header' | 'footer') => void;
};

const ProfessionalSellersForm: React.FC<ProfessionalSellersFormProps> = () => {
  const formRef = React.useRef<HTMLFormElement>(null);

  const { t } = useTranslation('professional-sellers');
  const { sendEvent } = useAnalytics();
  const { countryList } = useLocalizationQuery();

  const [formView, setFormView] = useState<number>(1);
  const [partOneIsValid, setPartOneIsValid] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const handleSendTracking = (label: string) => {
    const payload = {
      type: 'event',
      payload: {
        event: 'professional_seller_form',
        button_label: label,
        fom_tab: formView === 1 ? 'general_info' : 'business_info',
      },
    };

    sendEvent(payload);
  };

  const makeInitialValues = () => {
    const initialValues = {
      oid: hiddenInputs.oid,
      retURL: hiddenInputs.retURL,
      lead_source: hiddenInputs.lead_source,
      email: '',
      first_name: '',
      last_name: '',
      title: '',
      company: '',
      state: '',
      city: '',
      street: '',
      zip: '',
      url: '',
      debug: '',
      debugEmail: '',
      isSecondHandSeller: false,
    };

    if (isDebug) {
      initialValues.debug = hiddenInputs.debug;
      initialValues.debugEmail = hiddenInputs.debugEmail;
    }

    initialValues[FORM_FIELD_MATCHER.legalFormCompany] = '';
    initialValues[FORM_FIELD_MATCHER.taxIdNumber] = '';
    initialValues[FORM_FIELD_MATCHER.companyRegistrationNumber] = '';
    initialValues[FORM_FIELD_MATCHER.inventoryManagmentSystem] = '';
    initialValues[FORM_FIELD_MATCHER.notesFromSeller] = '';
    initialValues[FORM_FIELD_MATCHER.country] = '';
    initialValues[FORM_FIELD_MATCHER.inventoryMix] = [];
    initialValues[FORM_FIELD_MATCHER.businessType] = '';
    initialValues[FORM_FIELD_MATCHER.socialMediaAccount] = '';
    initialValues[FORM_FIELD_MATCHER.language] = '';
    initialValues[FORM_FIELD_MATCHER.depositForecast] = '';

    return initialValues;
  };

  return (
    <>
      <div className={clsx(styles.proSellersForm)}>
        <header className={clsx('innerContainer')}>
          <h1 className={clsx(styles.proSellersForm__title, 'vc-title-s')}>{t('PRO_SELLERS.FORM.TITLE')}</h1>
          <p className={clsx(styles.proSellersForm__intro, 'vc-text-l')}>{t('PRO_SELLERS.FORM.INTRO')}</p>
        </header>
        <ul className={clsx(styles.proSellersForm__steps)}>
          <li className={clsx(styles.proSellersForm__steps__item, formView === 1 && styles.active)}>
            <span className={clsx(styles.proSellersForm__steps__item__nb, 'vc-text-s')}>
              <i>1</i>
            </span>
            <span className={clsx('vc-text-l')}>{t('PRO_SELLERS.FORM.STEPS.GENERAL')}</span>
          </li>
          <li className={clsx(styles.proSellersForm__steps__item, formView === 2 && styles.active)}>
            <span className={clsx(styles.proSellersForm__steps__item__nb, 'vc-text-s')}>
              <i>2</i>
            </span>
            <span className={clsx('vc-text-l')}>{t('PRO_SELLERS.FORM.STEPS.BUSINESS')}</span>
          </li>
        </ul>
        <iframe
          title="validation"
          name="validation"
          style={{ display: 'none' }}
        />
        <Formik
          initialValues={makeInitialValues()}
          validationSchema={Schema}
          onSubmit={(_, { setSubmitting }) => {
            handleSendTracking('submit');
            const form = formRef.current;
            if (form) {
              form.submit();
              setSubmitting(false);
              setIsModalOpen(true);
            }
          }}
          validate={(values) => {
            const { email, first_name, last_name, title, company, city, street, zip } = values;
            const country = values[FORM_FIELD_MATCHER.country];
            if (email && first_name && last_name && title && company && country && city && street && zip) {
              setPartOneIsValid(true);
            } else {
              setPartOneIsValid(false);
            }
          }}
        >
          {({ isValid }) => (
            <Form
              ref={formRef}
              acceptCharset="utf-8"
              className={clsx(styles.proSellersForm__form, 'innerContainer')}
              action={`${Environment.salesForcePostUrl}`}
              method="POST"
              target="validation"
            >
              <input
                type="hidden"
                name="oid"
                value={hiddenInputs.oid}
              />
              <input
                type="hidden"
                name="retURL"
                value={hiddenInputs.retURL}
              />
              <input
                type="hidden"
                name="lead_source"
                value={hiddenInputs.lead_source}
              />
              {isDebug && (
                <input
                  type="hidden"
                  name="debug"
                  value={hiddenInputs.debug}
                />
              )}
              {isDebug && (
                <input
                  type="hidden"
                  name="debugEmail"
                  value={hiddenInputs.debugEmail}
                />
              )}
              <div
                className={clsx(
                  styles.proSellersForm__form__part,
                  formView === 1 && styles['proSellersForm__form__part--showned'],
                )}
              >
                <Field name="email">
                  {(props: FieldProps) => (
                    <InputComponent
                      {...props}
                      label={t('PRO_SELLERS.FORM.LABELS.EMAIL')}
                      placeholder={t('PRO_SELLERS.FORM.PLACEHOLDER.EMAIL')}
                      type="email"
                      required
                      handleSendTracking={() => handleSendTracking('email')}
                    />
                  )}
                </Field>
                <div className={clsx(styles.proSellersForm__form__row, styles['proSellersForm__form__row--2cols'])}>
                  <Field name="first_name">
                    {(props: FieldProps) => (
                      <InputComponent
                        {...props}
                        label={t('PRO_SELLERS.FORM.LABELS.FIRST_NAME')}
                        placeholder={t('PRO_SELLERS.FORM.PLACEHOLDER.FIRST_NAME')}
                        type="text"
                        required
                        handleSendTracking={() => handleSendTracking('first_name')}
                      />
                    )}
                  </Field>
                  <Field name="last_name">
                    {(props: FieldProps) => (
                      <InputComponent
                        {...props}
                        label={t('PRO_SELLERS.FORM.LABELS.LAST_NAME')}
                        placeholder={t('PRO_SELLERS.FORM.PLACEHOLDER.LAST_NAME')}
                        type="text"
                        required
                        handleSendTracking={() => handleSendTracking('last_name')}
                      />
                    )}
                  </Field>
                </div>
                <Field name="title">
                  {(props: FieldProps) => (
                    <InputComponent
                      {...props}
                      label={t('PRO_SELLERS.FORM.LABELS.TITLE')}
                      placeholder={null}
                      type="text"
                      required
                      handleSendTracking={() => handleSendTracking('title')}
                    />
                  )}
                </Field>
                <Field name="company">
                  {(props: FieldProps) => (
                    <InputComponent
                      {...props}
                      label={t('PRO_SELLERS.FORM.LABELS.COMPANY')}
                      placeholder={null}
                      type="text"
                      required
                      handleSendTracking={() => handleSendTracking('company')}
                    />
                  )}
                </Field>
                <Field name={FORM_FIELD_MATCHER.legalFormCompany}>
                  {(props: FieldProps) => (
                    <InputComponent
                      {...props}
                      label={t('PRO_SELLERS.FORM.LABELS.LEGAL_FORM_COMPANY')}
                      placeholder={null}
                      type="text"
                      handleSendTracking={() => handleSendTracking('legal_form_company')}
                    >
                      <span className={clsx(styles.proSellersForm__form__row__subtext, 'vc-text-s')}>
                        {t('PRO_SELLERS.FORM.PLACEHOLDER.LEGAL_FORM_COMPANY')}
                      </span>
                    </InputComponent>
                  )}
                </Field>
                <div className={clsx(styles.proSellersForm__form__row, styles['proSellersForm__form__row--2cols'])}>
                  <Field name={FORM_FIELD_MATCHER.country}>
                    {(props: FieldProps) => (
                      <InputComponent
                        {...props}
                        label={t('PRO_SELLERS.FORM.LABELS.COUNTRY')}
                        placeholder={null}
                        type="select"
                        required
                        handleSendTracking={() => handleSendTracking('country')}
                        options={countryList.map((country) => ({
                          name: country.name,
                          id: country.isoCode,
                          value: country.isoCode,
                        }))}
                      />
                    )}
                  </Field>
                  <Field name="state">
                    {(props: FieldProps) => (
                      <InputComponent
                        {...props}
                        label={t('PRO_SELLERS.FORM.LABELS.STATE_PROVINCE')}
                        placeholder={null}
                        type="text"
                        handleSendTracking={() => handleSendTracking('state')}
                        required={false}
                      />
                    )}
                  </Field>
                </div>
                <div className={clsx(styles.proSellersForm__form__row, styles['proSellersForm__form__row--2cols'])}>
                  <Field name="city">
                    {(props: FieldProps) => (
                      <InputComponent
                        {...props}
                        label={t('PRO_SELLERS.FORM.LABELS.CITY')}
                        placeholder={null}
                        type="text"
                        required
                        handleSendTracking={() => handleSendTracking('city')}
                      />
                    )}
                  </Field>
                  <Field name="street">
                    {(props: FieldProps) => (
                      <InputComponent
                        {...props}
                        label={t('PRO_SELLERS.FORM.LABELS.STREET')}
                        placeholder={null}
                        type="text"
                        required
                        handleSendTracking={() => handleSendTracking('street')}
                      />
                    )}
                  </Field>
                </div>
                <div className={clsx(styles.proSellersForm__form__row, styles['proSellersForm__form__row--2cols'])}>
                  <Field name="zip">
                    {(props: FieldProps) => (
                      <InputComponent
                        {...props}
                        label={t('PRO_SELLERS.FORM.LABELS.POSTAL_CODE')}
                        placeholder={null}
                        type="text"
                        required
                        handleSendTracking={() => handleSendTracking('zip')}
                      />
                    )}
                  </Field>
                </div>
                <BottomInformation />
                <button
                  className={clsx(
                    'vc-btn',
                    'vc-btn--primary',
                    'vc-btn--large',
                    'vc-btn--full',
                    'vc-btn--notfull--md-up',
                    styles.proSellersForm__form__submit,
                  )}
                  type="button"
                  name="Next"
                  onClick={() => setFormView(2)}
                  disabled={!partOneIsValid}
                >
                  {t('PRO_SELLERS.FORM.NEXT_BTN')}
                </button>
              </div>
              <div
                className={clsx(
                  styles.proSellersForm__form__part,
                  formView === 2 && styles['proSellersForm__form__part--showned'],
                )}
              >
                <Field name={FORM_FIELD_MATCHER.taxIdNumber}>
                  {(props: FieldProps) => (
                    <InputComponent
                      {...props}
                      label={t('PRO_SELLERS.FORM.LABELS.TAX_ID')}
                      placeholder={null}
                      type="text"
                      required
                      handleSendTracking={() => handleSendTracking('tax_id_number')}
                    />
                  )}
                </Field>
                <Field name={FORM_FIELD_MATCHER.companyRegistrationNumber}>
                  {(props: FieldProps) => (
                    <InputComponent
                      {...props}
                      label={t('PRO_SELLERS.FORM.LABELS.COMPANY_REGISTRATION_NB')}
                      placeholder={null}
                      type="text"
                      required
                      handleSendTracking={() => handleSendTracking('company_registration_number')}
                    />
                  )}
                </Field>
                <Field name={FORM_FIELD_MATCHER.businessType}>
                  {(props: FieldProps) => (
                    <InputComponent
                      {...props}
                      label={t('PRO_SELLERS.FORM.LABELS.BUSINESS_TYPE')}
                      placeholder={null}
                      type="select"
                      required
                      handleSendTracking={() => handleSendTracking('business_type')}
                      options={businessTypeOptions.map((option) => ({
                        name: t(option.name),
                        id: option.id,
                        value: option.value,
                      }))}
                    />
                  )}
                </Field>
                <Field name="url">
                  {(props: FieldProps) => (
                    <InputComponent
                      {...props}
                      label={t('PRO_SELLERS.FORM.LABELS.LINK_TO_WEBSITE')}
                      placeholder={null}
                      type="text"
                      required={false}
                      handleSendTracking={() => handleSendTracking('url')}
                    />
                  )}
                </Field>
                <Field name={FORM_FIELD_MATCHER.socialMediaAccount}>
                  {(props: FieldProps) => (
                    <InputComponent
                      {...props}
                      label={t('PRO_SELLERS.FORM.LABELS.LINK_TO_SOCIAL_MEDIA')}
                      placeholder={null}
                      type="text"
                      required={false}
                      handleSendTracking={() => handleSendTracking('social_media_account')}
                    />
                  )}
                </Field>
                <div className={clsx('vc-formUnit', styles.proSellersForm__form__row, 'vc-text-s')}>
                  <div className={clsx(styles.proSellersForm__form__boxesTitle, 'vc-inputText-label--required')}>
                    {t('PRO_SELLERS.FORM.LABELS.INVENTORY_MIX')}
                  </div>
                  <ul className={clsx(styles.proSellersForm__form__boxes)}>
                    <li className={clsx(styles.proSellersForm__form__boxes__item)}>
                      <Field name={FORM_FIELD_MATCHER.inventoryMix}>
                        {(props: FieldProps) => (
                          <InputComponent
                            {...props}
                            id="inventory_clothing"
                            label={t('PRO_SELLERS.FORM.LABELS.INVENTORY_MIX.CLOTHING')}
                            placeholder={null}
                            type="checkbox_group"
                            value="Clothing"
                            required={false}
                            handleSendTracking={() => handleSendTracking('inventory_clothing')}
                          />
                        )}
                      </Field>
                    </li>
                    <li className={clsx(styles.proSellersForm__form__boxes__item)}>
                      <Field name={FORM_FIELD_MATCHER.inventoryMix}>
                        {(props: FieldProps) => (
                          <InputComponent
                            {...props}
                            id="inventory_shoes"
                            label={t('PRO_SELLERS.FORM.LABELS.INVENTORY_MIX.SHOES')}
                            placeholder={null}
                            type="checkbox_group"
                            value="Shoes"
                            required={false}
                            handleSendTracking={() => handleSendTracking('inventory_shoes')}
                          />
                        )}
                      </Field>
                    </li>
                    <li className={clsx(styles.proSellersForm__form__boxes__item)}>
                      <Field name={FORM_FIELD_MATCHER.inventoryMix}>
                        {(props: FieldProps) => (
                          <InputComponent
                            {...props}
                            id="inventory_accessories"
                            label={t('PRO_SELLERS.FORM.LABELS.INVENTORY_MIX.ACCESSORIES')}
                            placeholder={null}
                            type="checkbox_group"
                            value="Accessories"
                            required={false}
                            handleSendTracking={() => handleSendTracking('inventory_accessories')}
                          />
                        )}
                      </Field>
                    </li>
                    <li className={clsx(styles.proSellersForm__form__boxes__item)}>
                      <Field name={FORM_FIELD_MATCHER.inventoryMix}>
                        {(props: FieldProps) => (
                          <InputComponent
                            {...props}
                            id="inventory_bags"
                            label={t('PRO_SELLERS.FORM.LABELS.INVENTORY_MIX.BAGS')}
                            placeholder={null}
                            type="checkbox_group"
                            value="Bags"
                            required={false}
                            handleSendTracking={() => handleSendTracking('inventory_bags')}
                          />
                        )}
                      </Field>
                    </li>
                    <li className={clsx(styles.proSellersForm__form__boxes__item)}>
                      <Field name={FORM_FIELD_MATCHER.inventoryMix}>
                        {(props: FieldProps) => (
                          <InputComponent
                            {...props}
                            id="inventory_jewelry"
                            label={t('PRO_SELLERS.FORM.LABELS.INVENTORY_MIX.JEWELRY')}
                            placeholder={null}
                            type="checkbox_group"
                            value="Jewelry"
                            required={false}
                            handleSendTracking={() => handleSendTracking('inventory_jewelry')}
                          />
                        )}
                      </Field>
                    </li>
                    <li className={clsx(styles.proSellersForm__form__boxes__item)}>
                      <Field name={FORM_FIELD_MATCHER.inventoryMix}>
                        {(props: FieldProps) => (
                          <InputComponent
                            {...props}
                            id="inventory_watches"
                            label={t('PRO_SELLERS.FORM.LABELS.INVENTORY_MIX.WATCHES')}
                            placeholder={null}
                            type="checkbox_group"
                            value="Watches"
                            required={false}
                            handleSendTracking={() => handleSendTracking('inventory_watches')}
                          />
                        )}
                      </Field>
                    </li>
                    <li className={clsx(styles.proSellersForm__form__boxes__item)}>
                      <Field name={FORM_FIELD_MATCHER.inventoryMix}>
                        {(props: FieldProps) => (
                          <InputComponent
                            {...props}
                            id="inventory_kids"
                            label={t('PRO_SELLERS.FORM.LABELS.INVENTORY_MIX.KIDS')}
                            placeholder={null}
                            type="checkbox_group"
                            value="Kids"
                            required={false}
                            handleSendTracking={() => handleSendTracking('inventory_kids')}
                          />
                        )}
                      </Field>
                    </li>
                  </ul>
                </div>
                <Field name={FORM_FIELD_MATCHER.depositForecast}>
                  {(props: FieldProps) => (
                    <InputComponent
                      {...props}
                      label={t('PRO_SELLERS.FORM.LABELS.DEPOSIT_FORECAST')}
                      placeholder={null}
                      type="number"
                      required
                      handleSendTracking={() => handleSendTracking('deposit_forecast')}
                    />
                  )}
                </Field>
                <Field name={FORM_FIELD_MATCHER.inventoryManagmentSystem}>
                  {(props: FieldProps) => (
                    <InputComponent
                      {...props}
                      label={t('PRO_SELLERS.FORM.LABELS.INVENTORY_MANAGEMENT_SYSTEM')}
                      placeholder={null}
                      type="text"
                      required={false}
                      handleSendTracking={() => handleSendTracking('inventory_management_system')}
                    />
                  )}
                </Field>
                <Field name={FORM_FIELD_MATCHER.notesFromSeller}>
                  {(props: FieldProps) => (
                    <InputComponent
                      {...props}
                      label={t('PRO_SELLERS.FORM.LABELS.NOTES_FROM_SELLER')}
                      placeholder={null}
                      type="textarea"
                      required={false}
                      handleSendTracking={() => handleSendTracking('notes_from_seller')}
                    />
                  )}
                </Field>
                <Field name={FORM_FIELD_MATCHER.language}>
                  {(props: FieldProps) => (
                    <InputComponent
                      {...props}
                      label={t('PRO_SELLERS.FORM.LABELS.LANGUAGE')}
                      placeholder={null}
                      type="select"
                      required
                      handleSendTracking={() => handleSendTracking('language')}
                      options={languageOptions.map((option) => ({
                        name: t(option.name),
                        id: option.id,
                        value: option.value,
                      }))}
                    />
                  )}
                </Field>
                <Field name="isSecondHandSeller">
                  {(props: FieldProps) => (
                    <InputComponent
                      {...props}
                      label={t('PRO_SELLERS.FORM.IS_SECOND_HAND_SELLER')}
                      placeholder={null}
                      type="checkbox"
                      required
                    />
                  )}
                </Field>

                <BottomInformation />

                <div className={clsx(styles.proSellersForm__form__cta)}>
                  <button
                    onClick={() => setFormView(1)}
                    className={clsx(styles.proSellersForm__form__backBtn)}
                    type="button"
                    name="Back"
                  >
                    <Icon
                      name="arrow-head-left-thick"
                      className={clsx(styles.proSellersForm__form__backBtn__icon)}
                      ariaHidden
                    />
                    <span>{t('PRO_SELLERS.FORM.BACK_BTN')}</span>
                  </button>
                  <input
                    className={clsx('vc-btn', 'vc-btn--primary', 'vc-btn--large', styles.proSellersForm__form__submit)}
                    type="submit"
                    name="Submit"
                    disabled={!isValid}
                    value={t('PRO_SELLERS.FORM.SUBMIT_BTN')}
                  />
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>

      <ValidationModal
        isOpen={isModalOpen}
        onClose={setIsModalOpen}
        // Always set to success as logic was removed in NGX pending help from Salesforce
        type="success"
      />
    </>
  );
};

export default ProfessionalSellersForm;
